/**
 * AI 통역 PT(발표) 컴포저블
 *
 * 1 source → N target 다국어 동시 통역.
 *   - HTTP: POST /api/v1/miras/voice/pt/token  (OctoberCMS Proxy)
 *   - WS:   wss://.../voice/ws/pt              (MIRAS 직결)
 *
 * 상태(state)는 Vue 컴포넌트의 reactive 객체로 사용한다 (Vue 2 .observable 또는
 * Composition API ref 모두 호환되도록, 컴포넌트에서 reactive wrap 한다).
 *
 * 메시지 흐름:
 *   클라 → 서버: { type:'auth', token }
 *                { type:'config', title, meta }
 *                { type:'audio_chunk', data:<base64 PCM16LE 16k> }
 *                { type:'pause' } | { type:'resume' } | { type:'stop' } | { type:'ping' }
 *   서버 → 클라: { type:'auth_ok', session_id, source_language, target_languages, pt_mode }
 *                { type:'ready' }
 *                { type:'stt_partial', text }
 *                { type:'turn', ord, original, translations:{lang:text}, model_used:{lang:'flash-lite'|'flash'|null}, status }
 *                { type:'mode_changed', from, to, reason }
 *                { type:'paused' } | { type:'resumed' } | { type:'pong' }
 *                { type:'error', message }
 *
 * 옵셔널 체이닝(`?.`) 및 nullish(`??`) 사용 금지 — 프로젝트 규약에 따라 if 분기로 작성.
 */

import HttpRequest from '@/util/HttpRequest'
import config from '/public/config'

const WORKLET_URL = '/pcm-worklet.js'
const TARGET_SR = 16000


export function useVoicePtBroadcast(options) {
  const opts = options ? options : {}
  const onStateChange = typeof opts.onStateChange === 'function' ? opts.onStateChange : function () {}
  const onEvent = typeof opts.onEvent === 'function' ? opts.onEvent : function () {}

  // ─── 내부 상태 ───────────────────────────────────────────────
  const state = {
    // 연결
    isConnecting: false,
    isStreaming: false,
    isPaused: false,
    micPermission: 'unknown',  // 'granted' | 'denied' | 'prompt' | 'unknown'

    // 세션
    sessionId: null,
    mode: 'pt',
    ptMode: 'stt',           // 'live' | 'stt' (서버 확정 후 갱신)
    autoDowngradedToStt: false,

    // 발표 메타
    title: '',
    sourceLang: 'ko',
    targetLangs: [],         // ['en','vi',...]

    // 격자 (UI 측 cells 와 1:1 매핑되는 보조 데이터)
    // 각 항목: { lang, text, lastTurnId, lastModel, failed, updatedAt }
    cellsByLang: {},

    // 발표자 미리보기 (partial STT)
    sourcePreview: '',

    // 발화 이력 (UI 측 turn 카드용)
    turns: [],               // [{ ord, original, translations, model_used, status, at }]

    // 경과/오류
    startedAt: null,
    elapsedSec: 0,
    error: null,
  }

  // ─── 내부 핸들/오디오 ────────────────────────────────────────
  let ws = null
  let audioCtx = null
  let mediaStream = null
  let sourceNode = null
  let workletNode = null
  let analyserNode = null
  let timerInterval = null
  // 무음 감지: 마지막으로 사람 목소리/유의미한 신호가 감지된 시각 (ms)
  let lastVoiceTs = 0
  let silenceWarned = false
  let analyserInterval = null
  const SILENCE_WARN_MS = 12000    // 12초간 입력 없으면 경고 (발표 휴지 고려)
  const SILENCE_RMS_THRESHOLD = 0.005  // 마이크 게인 낮은 환경 고려해 완화

  // ─── 헬퍼 ────────────────────────────────────────────────────
  function _notify() {
    try { onStateChange(state) } catch (e) { /* ignore */ }
  }

  function _emit(evt) {
    try { onEvent(evt) } catch (e) { /* ignore */ }
  }

  function _setError(msg) {
    state.error = msg
    _notify()
  }

  // 격자별 누적 표시를 위해 각 lang 의 최근 turn 들을 entries[] 로 보관.
  // 가장 최근 entry 가 배열 끝(현재 큰 글씨). 위로 갈수록(인덱스 작을수록) 과거.
  // 발표 화면 시인성을 우선해 4개(현재 + 직전 3개)만 유지.
  const CELL_HISTORY_MAX = 4

  function _resetCells(targetLangs) {
    const next = {}
    for (let i = 0; i < targetLangs.length; i++) {
      const lang = targetLangs[i]
      next[lang] = {
        lang: lang,
        // entries: [{ id, text, model, failed, at }]
        entries: [],
        lastTurnId: null,
        lastModel: null,
        failed: false,
        updatedAt: null,
      }
    }
    state.cellsByLang = next
  }

  function _applyTurn(turn) {
    // turn.translations: { en: '...', vi: '...' }
    // turn.model_used:   { en: 'flash-lite', vi: null, ... }
    const translations = turn && turn.translations ? turn.translations : {}
    const modelUsed = turn && turn.model_used ? turn.model_used : {}
    const ts = Date.now()
    const turnId = turn.turn_id !== undefined ? turn.turn_id : null

    const keys = Object.keys(state.cellsByLang)
    for (let i = 0; i < keys.length; i++) {
      const lang = keys[i]
      const cell = state.cellsByLang[lang]
      if (translations[lang] !== undefined) {
        const text = translations[lang]
        const model = modelUsed[lang] !== undefined ? modelUsed[lang] : null
        const failed = (!text) || (model === null)
        cell.entries.push({
          id: turnId !== null ? (turnId + '_' + lang) : ('local_' + ts + '_' + lang),
          text: text || '',
          model: model,
          failed: failed,
          at: ts,
        })
        // 최대 길이 유지 (앞쪽 = 가장 오래된 항목 제거)
        if (cell.entries.length > CELL_HISTORY_MAX) {
          cell.entries.splice(0, cell.entries.length - CELL_HISTORY_MAX)
        }
        cell.lastModel = model
        cell.failed = failed
        cell.updatedAt = ts
        cell.lastTurnId = turnId
      }
    }

    state.turns.push({
      ord: turn.ord,
      original: turn.original,
      translations: translations,
      model_used: modelUsed,
      status: turn.status,
      at: ts,
    })
    // 메모리 가드: 발표가 길어지면 최근 200개만 유지
    if (state.turns.length > 200) {
      state.turns.splice(0, state.turns.length - 200)
    }
  }

  // ─── 마이크 권한 ─────────────────────────────────────────────
  async function checkMicPermission() {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' })
        state.micPermission = result.state
        _notify()
        return result.state
      }
    } catch (e) { /* 일부 브라우저에서 미지원 */ }
    return state.micPermission
  }

  // ─── 토큰 발급 → WS 연결 → AudioWorklet 시작 ────────────────
  async function start(params) {
    if (state.isStreaming || state.isConnecting) return

    const p = params ? params : {}
    state.title = p.title ? p.title : ''
    state.sourceLang = p.sourceLang ? p.sourceLang : 'ko'
    state.targetLangs = Array.isArray(p.targetLangs) ? p.targetLangs.slice() : []
    state.ptMode = p.ptMode === 'live' ? 'live' : 'stt'

    if (state.targetLangs.length === 0) {
      _setError('번역 대상 언어를 1개 이상 선택해주세요.')
      return
    }

    state.isConnecting = true
    state.error = null
    state.sourcePreview = ''
    state.turns = []
    state.autoDowngradedToStt = false
    _resetCells(state.targetLangs)
    _notify()

    try {
      // 1) 토큰 발급 + getUserMedia 병렬
      //    HttpRequest.post 는 axios.response.data 를 이미 풀어서 반환하므로
      //    tokenData = 응답 body 자체.
      const [tokenData, micStream] = await Promise.all([
        HttpRequest.post('/api/v1/miras/voice/pt/token', {
          source_language: state.sourceLang,
          target_languages: state.targetLangs,
          pt_mode: state.ptMode,
        }),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ])

      if (!tokenData || !tokenData.ws_token) {
        throw new Error('PT 토큰 발급 실패')
      }
      mediaStream = micStream

      // 2) 선택된 마이크 장치 라벨 알림 (어떤 입력이 잡혔는지 가시화)
      try {
        const tracks = mediaStream.getAudioTracks ? mediaStream.getAudioTracks() : []
        if (tracks.length > 0) {
          const label = tracks[0].label ? tracks[0].label : '(이름 없음)'
          _emit({ type: 'mic_info', label: label })
        }
      } catch (e) { /* ignore */ }

      // 3) WebSocket 연결
      await _connectWebSocket(tokenData.ws_token)

      // 4) AudioWorklet 시작
      await _startAudioPipeline()

      state.isConnecting = false
      state.isStreaming = true
      state.startedAt = Date.now()
      _startTimer()
      _notify()
      _emit({ type: 'started' })

    } catch (err) {
      state.isConnecting = false
      _setError((err && err.message) ? err.message : '시작 실패')
      await _cleanup()
      _emit({ type: 'error', message: state.error })
    }
  }

  function _connectWebSocket(wsToken) {
    return new Promise(function (resolve, reject) {
      const baseUrl = config && config.BASE_URL ? config.BASE_URL : ''
      const wsUrl = baseUrl
        .replace('https://', 'wss://')
        .replace('http://', 'ws://') + '/voice/ws/pt'

      try {
        ws = new WebSocket(wsUrl)
      } catch (e) {
        reject(new Error('WebSocket 생성 실패: ' + e.message))
        return
      }

      // Promise 가 아직 settle 되지 않았는지 추적.
      // auth_ok 만으로는 연결 확립이 아님 — 'ready' 가 와야 백엔드 준비 완료.
      // ready 전에 error/close 가 발생하면 반드시 reject 해야 한다.
      let resolved = false
      const settleResolve = function () {
        if (resolved) return
        resolved = true
        resolve()
      }
      const settleReject = function (err) {
        if (resolved) return
        resolved = true
        reject(err)
      }
      let authOk = false

      ws.onopen = function () {
        // 인증 메시지
        ws.send(JSON.stringify({ type: 'auth', token: wsToken }))
        // 메타 정보 전송 (선택)
        ws.send(JSON.stringify({
          type: 'config',
          title: state.title,
          meta: {
            target_langs: state.targetLangs,
            source_lang: state.sourceLang,
            pt_mode: state.ptMode,
          },
        }))
      }

      ws.onmessage = function (event) {
        let msg = null
        try { msg = JSON.parse(event.data) } catch (e) { return }
        if (!msg || !msg.type) return

        if (msg.type === 'auth_ok') {
          state.sessionId = msg.session_id ? msg.session_id : null
          state.ptMode = msg.pt_mode ? msg.pt_mode : state.ptMode
          if (Array.isArray(msg.target_languages) && msg.target_languages.length > 0) {
            state.targetLangs = msg.target_languages.slice()
            _resetCells(state.targetLangs)
          }
          authOk = true
          _notify()
          return
        }

        if (msg.type === 'ready') {
          // 서버가 모든 백엔드 의존성 준비 완료
          _emit({ type: 'ready' })
          settleResolve()
          return
        }

        if (msg.type === 'stt_partial') {
          state.sourcePreview = msg.text ? msg.text : ''
          _notify()
          // STT partial 도착 = 마이크 신호가 서버까지 흘러 인식되는 확실한 증거.
          // 무음 감지 타이머를 활성 입력으로 갱신해 잘못된 토스트를 막는다.
          if (state.sourcePreview) {
            lastVoiceTs = Date.now()
            silenceWarned = false
          }
          return
        }

        if (msg.type === 'turn') {
          _applyTurn(msg)
          state.sourcePreview = ''   // final 도착했으므로 미리보기는 초기화
          _notify()
          _emit({ type: 'turn', turn: msg })
          // turn 도착 = 발화가 완전히 인식·번역됨. 무음 타이머 갱신.
          lastVoiceTs = Date.now()
          silenceWarned = false
          return
        }

        if (msg.type === 'cell_partial') {
          // ModeA(Live × N): 격자별 진행 중 통역 텍스트 (덮어쓰기)
          _emit({ type: 'cell_partial', lang: msg.lang, text: msg.text })
          lastVoiceTs = Date.now()
          silenceWarned = false
          return
        }

        if (msg.type === 'cell_final') {
          // ModeA(Live × N): 격자별 최종 통역 텍스트 (한 turn 끝)
          _emit({ type: 'cell_final', lang: msg.lang, text: msg.text })
          state.sourcePreview = ''
          lastVoiceTs = Date.now()
          silenceWarned = false
          return
        }

        if (msg.type === 'cell_error') {
          _emit({ type: 'cell_error', lang: msg.lang, message: msg.message })
          return
        }

        if (msg.type === 'mode_changed') {
          if (msg.to === 'stt') state.autoDowngradedToStt = true
          state.ptMode = msg.to ? msg.to : state.ptMode
          _notify()
          _emit({ type: 'mode_changed', from: msg.from, to: msg.to, reason: msg.reason })
          return
        }

        if (msg.type === 'paused') {
          state.isPaused = true
          _notify()
          return
        }
        if (msg.type === 'resumed') {
          state.isPaused = false
          _notify()
          return
        }
        if (msg.type === 'pong') return

        if (msg.type === 'error' || msg.type === 'auth_error') {
          const m = msg.message ? msg.message : '서버 오류'
          _setError(m)
          _emit({ type: 'error', message: m })
          // ready 전이라면(= 아직 settle 안 됨) 항상 reject — auth_ok 만으로는
          // 연결 확립이 아니므로 authOk 만 보지 말고 resolved 플래그로 판단.
          if (!resolved) {
            try { ws.close() } catch (e) { /* ignore */ }
            settleReject(new Error(m))
          }
          return
        }
      }

      ws.onerror = function () {
        _setError('WebSocket 연결 오류')
        settleReject(new Error('WebSocket 연결 오류'))
      }

      ws.onclose = function (ev) {
        // ready 전 close 면 reject. ready 이후면 단순 정상 종료 처리.
        state.isStreaming = false
        _stopTimer()
        _notify()
        _emit({ type: 'closed' })
        if (!resolved) {
          const code = ev && ev.code ? ev.code : ''
          const reason = ev && ev.reason ? ev.reason : ''
          settleReject(new Error('WebSocket 연결이 ready 전에 종료됨 (code=' + code + ' ' + reason + ')'))
        }
      }
      // 안전망: 8초 내 ready 가 오지 않으면 reject (서버가 stuck 한 경우)
      setTimeout(function () {
        if (!resolved) {
          _setError('WebSocket ready 타임아웃 (8초). 서버 의존성을 확인하세요.')
          try { ws.close() } catch (e) { /* ignore */ }
          settleReject(new Error('WebSocket ready 타임아웃'))
        }
      }, 8000)
    })
  }

  async function _startAudioPipeline() {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) throw new Error('AudioContext 미지원 브라우저')

    try {
      audioCtx = new Ctx({ sampleRate: TARGET_SR })
    } catch (e) {
      audioCtx = new Ctx()
    }

    await audioCtx.audioWorklet.addModule(WORKLET_URL)
    workletNode = new AudioWorkletNode(audioCtx, 'pcm-worklet', {
      processorOptions: { inputSampleRate: audioCtx.sampleRate },
    })

    workletNode.port.onmessage = function (e) {
      if (state.isPaused) return
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      // pcm-worklet 은 e.data 에 ArrayBuffer(PCM16 LE 16k mono)를 직접 보낸다.
      // base64 인코딩 후 JSON 메시지로 송신.
      if (!e.data) return
      try {
        const base64 = _bufToBase64(e.data)
        ws.send(JSON.stringify({ type: 'audio_chunk', data: base64 }))
      } catch (err) {
        /* ignore — 짧은 끊김은 무시 */
      }
    }

    sourceNode = audioCtx.createMediaStreamSource(mediaStream)
    sourceNode.connect(workletNode)
    // workletNode 는 destination에 연결하지 않음 (출력 불필요, 모니터 피드백 방지)

    // 무음 모니터링용 AnalyserNode (PCM 송신 경로와 별개로 RMS 측정)
    try {
      analyserNode = audioCtx.createAnalyser()
      analyserNode.fftSize = 1024
      sourceNode.connect(analyserNode)
    } catch (e) { analyserNode = null }

    lastVoiceTs = Date.now()
    silenceWarned = false
    _startSilenceMonitor()
  }

  function _startSilenceMonitor() {
    if (analyserInterval) clearInterval(analyserInterval)
    if (!analyserNode) return
    const buf = new Float32Array(analyserNode.fftSize)
    analyserInterval = setInterval(function () {
      if (!analyserNode || state.isPaused) return
      try {
        analyserNode.getFloatTimeDomainData(buf)
        let sum = 0
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
        const rms = Math.sqrt(sum / buf.length)
        if (rms >= SILENCE_RMS_THRESHOLD) {
          lastVoiceTs = Date.now()
          // 무음이 풀리면 경고 플래그 해제 (다시 침묵하면 다시 알림)
          silenceWarned = false
        } else if (!silenceWarned && (Date.now() - lastVoiceTs > SILENCE_WARN_MS)) {
          silenceWarned = true
          _emit({
            type: 'silence_warning',
            message:
              '마이크 입력이 감지되지 않습니다. 입력 장치/볼륨을 확인해주세요. ' +
              '스테레오 믹스로 시스템 사운드를 캡쳐 중이라면, 기본 입력 장치로 지정 후 페이지를 새로고침하세요.',
          })
        }
      } catch (e) { /* ignore */ }
    }, 1000)
  }

  function _stopSilenceMonitor() {
    if (analyserInterval) {
      clearInterval(analyserInterval)
      analyserInterval = null
    }
  }

  // PCM ArrayBuffer → base64. 큰 버퍼에서도 stack overflow 방지를 위해 청크 처리.
  function _bufToBase64(buf) {
    const bytes = new Uint8Array(buf)
    let binary = ''
    const chunkSize = 8192
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
    }
    return btoa(binary)
  }

  // ─── 컨트롤 ──────────────────────────────────────────────────
  function pause() {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    state.isPaused = true
    try { ws.send(JSON.stringify({ type: 'pause' })) } catch (e) { /* ignore */ }
    _notify()
  }

  function resume() {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    state.isPaused = false
    try { ws.send(JSON.stringify({ type: 'resume' })) } catch (e) { /* ignore */ }
    _notify()
  }

  async function stop() {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'stop' })) } catch (e) { /* ignore */ }
    }
    await _cleanup()
    state.isStreaming = false
    _stopTimer()
    _notify()
    _emit({ type: 'stopped' })
  }

  async function reset() {
    await stop()
    state.sessionId = null
    state.sourcePreview = ''
    state.turns = []
    state.autoDowngradedToStt = false
    state.error = null
    _resetCells(state.targetLangs)
    state.elapsedSec = 0
    _notify()
  }

  async function _cleanup() {
    _stopSilenceMonitor()
    if (workletNode) {
      try { workletNode.port.onmessage = null } catch (e) { /* ignore */ }
      try { workletNode.disconnect() } catch (e) { /* ignore */ }
      workletNode = null
    }
    if (analyserNode) {
      try { analyserNode.disconnect() } catch (e) { /* ignore */ }
      analyserNode = null
    }
    if (sourceNode) {
      try { sourceNode.disconnect() } catch (e) { /* ignore */ }
      sourceNode = null
    }
    if (audioCtx) {
      try { await audioCtx.close() } catch (e) { /* ignore */ }
      audioCtx = null
    }
    if (mediaStream) {
      try {
        const tracks = mediaStream.getTracks ? mediaStream.getTracks() : []
        for (let i = 0; i < tracks.length; i++) {
          try { tracks[i].stop() } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
      mediaStream = null
    }
    if (ws) {
      try { ws.close() } catch (e) { /* ignore */ }
      ws = null
    }
  }

  // ─── 타이머 ─────────────────────────────────────────────────
  function _startTimer() {
    _stopTimer()
    timerInterval = setInterval(function () {
      if (state.startedAt) {
        state.elapsedSec = Math.floor((Date.now() - state.startedAt) / 1000)
        _notify()
      }
    }, 1000)
  }

  function _stopTimer() {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }

  // ─── 발표 이력 REST API ──────────────────────────────────────
  // HttpRequest 의 메서드들은 axios.response.data 를 이미 풀어서 반환하므로
  // 결과 그 자체가 응답 body. 추가로 .data 를 풀지 않는다.
  async function fetchSessions(params) {
    const p = params ? params : {}
    const query = []
    if (p.limit !== undefined) query.push('limit=' + p.limit)
    if (p.offset !== undefined) query.push('offset=' + p.offset)
    const qs = query.length > 0 ? '?' + query.join('&') : ''
    return await HttpRequest.get('/api/v1/miras/voice/pt/sessions' + qs)
  }

  async function fetchSessionDetail(sessionId) {
    return await HttpRequest.get('/api/v1/miras/voice/pt/sessions/' + encodeURIComponent(sessionId))
  }

  async function fetchSessionTurns(sessionId, params) {
    const p = params ? params : {}
    const query = []
    if (p.limit !== undefined) query.push('limit=' + p.limit)
    if (p.offset !== undefined) query.push('offset=' + p.offset)
    const qs = query.length > 0 ? '?' + query.join('&') : ''
    return await HttpRequest.get(
      '/api/v1/miras/voice/pt/sessions/' + encodeURIComponent(sessionId) + '/turns' + qs
    )
  }

  async function renameSession(sessionId, title) {
    // HttpRequest 클래스에 patch 가 없으므로 put 으로 대체하거나 axios 직접 사용 필요.
    // OctoberCMS routes 는 PATCH 로 등록되어 있으므로, 임시로 put 을 시도하고
    // 실패 시 사용자가 메서드를 알맞게 추가하도록 한다.
    if (typeof HttpRequest.patch === 'function') {
      return await HttpRequest.patch(
        '/api/v1/miras/voice/pt/sessions/' + encodeURIComponent(sessionId),
        { title: title }
      )
    }
    // 폴백: put 으로 시도 (서버 라우트가 patch 만 있으면 405 가 날 수 있으므로
    // 그 경우 OctoberCMS routes 에 put 별칭을 추가하거나 HttpRequest 에 patch 메서드를 추가해야 함)
    return await HttpRequest.put(
      '/api/v1/miras/voice/pt/sessions/' + encodeURIComponent(sessionId),
      { title: title }
    )
  }

  async function deleteSession(sessionId) {
    return await HttpRequest.delete(
      '/api/v1/miras/voice/pt/sessions/' + encodeURIComponent(sessionId)
    )
  }

  // ─── 공개 API ───────────────────────────────────────────────
  return {
    state,
    checkMicPermission,
    start,
    pause,
    resume,
    stop,
    reset,
    fetchSessions,
    fetchSessionDetail,
    fetchSessionTurns,
    renameSession,
    deleteSession,
  }
}
